import {
  Alert,
  Badge,
  Box,
  Button,
  Drawer,
  Group,
  Loader,
  NumberInput,
  SegmentedControl,
  Select,
  Stack,
  Switch,
  Tabs,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { IconPlus, IconSearch } from '@tabler/icons-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../api';
import { useSession } from '../session';

interface Category {
  id: string;
  code: string;
  name: string;
  parent_id: string | null;
  is_group: boolean;
  kind: string | null;
  direction: 'income' | 'expense' | null;
  is_restricted: boolean;
  default_allocation_rule_id: string | null;
  is_active: boolean;
  aliases: number;
}
interface CategoryAlias { id: string; source: string; direction: 'income' | 'expense'; legacy_name: string; category_id: string; needs_review: boolean; note: string | null }
interface MetricAlias { id: string; source: string; legacy_department: string; legacy_indicator: string; legacy_type: string; metric_id: string | null; is_ignored: boolean; needs_review: boolean; note: string | null }
interface Rule { id: string; code: string; name: string; method: 'fixed' | 'revenue_share'; is_default: boolean; is_active: boolean; shares: { legal_entity_id: string; legal_entity: string; share: string }[] }
interface Catalogs {
  can_edit: boolean;
  can_edit_finance: boolean;
  categories: Category[];
  category_aliases: CategoryAlias[];
  metric_aliases: MetricAlias[];
  metrics: { id: string; name: string; group_name: string; department: string }[];
  departments: { id: string; code: string; name: string; is_active: boolean; sort_order: number }[];
  rules: Rule[];
}

const KINDS: Record<string, string> = {
  revenue: 'Выручка',
  other_income: 'Прочий доход',
  opex: 'Операционный расход',
  payroll: 'ФОТ',
  tax: 'Налоги и взносы',
  refund: 'Возврат пациенту',
  capex: 'Оборудование и вложения',
  owner_withdrawal: 'Вывод собственником',
  transfer: 'Перемещение между счетами',
  financing: 'Кредиты и займы',
};

async function call<T>(fn: () => Promise<T>, ok?: string): Promise<T | undefined> {
  try {
    const r = await fn();
    if (ok) notifications.show({ color: 'brand', message: ok });
    return r;
  } catch (e) {
    notifications.show({ color: 'red', message: (e as Error).message });
    return undefined;
  }
}

/** Справочники: соответствия старым таблицам, статьи, подразделения, правила деления. */
export function CatalogsPage() {
  const [data, setData] = useState<Catalogs | null>(null);
  const load = useCallback(async () => setData(await api.get<Catalogs>('/catalogs')), []);
  useEffect(() => {
    load().catch((e) => notifications.show({ color: 'red', message: e.message }));
  }, [load]);

  if (!data) {
    return (
      <Group justify="center" p="xl">
        <Loader />
      </Group>
    );
  }
  const review = data.category_aliases.filter((a) => a.needs_review).length + data.metric_aliases.filter((a) => a.needs_review).length;

  return (
    <Box p={{ base: 'md', sm: 'xl' }} maw={1100}>
      <Title order={2} mb="md">
        Справочники
      </Title>
      <Tabs defaultValue="aliases" keepMounted={false}>
        <Tabs.List mb="md">
          <Tabs.Tab value="aliases" rightSection={review ? <Badge size="sm" color="orange" variant="light">{review}</Badge> : null}>
            Соответствия
          </Tabs.Tab>
          <Tabs.Tab value="categories">Статьи</Tabs.Tab>
          <Tabs.Tab value="departments">Подразделения</Tabs.Tab>
          <Tabs.Tab value="rules">Деление общих затрат</Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value="aliases">
          <Aliases data={data} reload={load} />
        </Tabs.Panel>
        <Tabs.Panel value="categories">
          <Categories data={data} reload={load} />
        </Tabs.Panel>
        <Tabs.Panel value="departments">
          <Departments data={data} reload={load} />
        </Tabs.Panel>
        <Tabs.Panel value="rules">
          <Rules data={data} reload={load} />
        </Tabs.Panel>
      </Tabs>
    </Box>
  );
}

type Props = { data: Catalogs; reload: () => Promise<void> };

function Aliases({ data, reload }: Props) {
  const [kind, setKind] = useState<'dds' | 'rnp'>('dds');
  const [onlyReview, setOnlyReview] = useState(() => data.category_aliases.some((a) => a.needs_review) || data.metric_aliases.some((a) => a.needs_review));
  const [q, setQ] = useState('');
  const term = q.trim().toLowerCase();

  const catOptions = useMemo(() => {
    const groups = new Map(data.categories.filter((c) => c.is_group).map((g) => [g.id, g.name]));
    return (dir: string) =>
      data.categories
        .filter((c) => !c.is_group && c.is_active && (c.direction === dir || c.direction === null)) // перемещения и займы — в обе стороны
        .map((c) => ({ value: c.id, label: `${c.parent_id ? `${groups.get(c.parent_id)} · ` : ''}${c.name}` }));
  }, [data]);
  const metricOptions = useMemo(
    () => [{ value: '', label: 'Не переносить' }, ...data.metrics.map((m) => ({ value: m.id, label: `${m.department} · ${m.group_name} · ${m.name}` }))],
    [data],
  );

  const cats = data.category_aliases.filter((a) => (!onlyReview || a.needs_review) && (!term || a.legacy_name.toLowerCase().includes(term)));
  const mets = data.metric_aliases.filter(
    (a) => (!onlyReview || a.needs_review) && (!term || `${a.legacy_department} ${a.legacy_indicator} ${a.legacy_type}`.toLowerCase().includes(term)),
  );

  const save = (path: string, body: unknown) => call(() => api.patch(path, body), 'Соответствие сохранено').then(reload);

  return (
    <Stack gap="md">
      <Text size="sm" c="dimmed" maw={760}>
        Как строки старых таблиц (ДДС, РНП, ЭМ) попадают в статьи и показатели МИС. Изменение применится при следующей загрузке таблицы. Строки «проверить» —
        соответствия, в которых не уверены: выберите верную статью или нажмите «Верно».
      </Text>
      <Group gap="sm" wrap="wrap">
        <SegmentedControl value={kind} onChange={(v) => setKind(v as 'dds' | 'rnp')} data={[{ value: 'dds', label: 'Статьи ДДС' }, { value: 'rnp', label: 'Показатели РНП и ЭМ' }]} />
        <TextInput placeholder="Поиск" leftSection={<IconSearch size={16} />} value={q} onChange={(e) => setQ(e.currentTarget.value)} w={220} aria-label="Поиск" />
        <Switch label="Только «проверить»" checked={onlyReview} onChange={(e) => setOnlyReview(e.currentTarget.checked)} />
      </Group>
      <Box className="mis-card" style={{ overflow: 'hidden' }}>
        {(kind === 'dds' ? cats.length : mets.length) === 0 && (
          <Text c="dimmed" p="xl" ta="center">
            {onlyReview ? 'Всё проверено' : 'Ничего не найдено'}
          </Text>
        )}
        {kind === 'dds' &&
          cats.map((a) => (
            <Group key={a.id} data-alias={a.id} justify="space-between" wrap="wrap" gap="sm" px="md" py="sm" style={{ borderTop: '1px solid var(--mis-border)' }}>
              <div style={{ minWidth: 220, flex: 1 }}>
                <Group gap={6}>
                  <Text fw={500}>{a.legacy_name}</Text>
                  {a.needs_review && <Badge color="orange" variant="light" size="sm">проверить</Badge>}
                </Group>
                <Text size="xs" c="dimmed">
                  {a.source} · {a.direction === 'income' ? 'приход' : 'расход'}
                  {a.note ? ` · ${a.note}` : ''}
                </Text>
              </div>
              <Group gap="xs" wrap="nowrap">
                <Select
                  aria-label={`Статья для «${a.legacy_name}»`}
                  searchable
                  w={300}
                  data={catOptions(a.direction)}
                  value={a.category_id}
                  disabled={!data.can_edit}
                  allowDeselect={false}
                  onChange={(v) => v && v !== a.category_id && void save(`/catalogs/category-aliases/${a.id}`, { category_id: v, needs_review: false })}
                />
                {a.needs_review && data.can_edit && (
                  <Button variant="light" onClick={() => void save(`/catalogs/category-aliases/${a.id}`, { needs_review: false })}>
                    Верно
                  </Button>
                )}
              </Group>
            </Group>
          ))}
        {kind === 'rnp' &&
          mets.map((a) => (
            <Group key={a.id} data-alias={a.id} justify="space-between" wrap="wrap" gap="sm" px="md" py="sm" style={{ borderTop: '1px solid var(--mis-border)' }}>
              <div style={{ minWidth: 220, flex: 1 }}>
                <Group gap={6}>
                  <Text fw={500}>
                    {a.legacy_indicator} · {a.legacy_type}
                  </Text>
                  {a.needs_review && <Badge color="orange" variant="light" size="sm">проверить</Badge>}
                </Group>
                <Text size="xs" c="dimmed">
                  {a.source} · {a.legacy_department}
                  {a.note ? ` · ${a.note}` : ''}
                </Text>
              </div>
              <Group gap="xs" wrap="nowrap">
                <Select
                  aria-label={`Показатель для «${a.legacy_indicator} · ${a.legacy_type}»`}
                  searchable
                  w={300}
                  data={metricOptions}
                  value={a.metric_id ?? ''}
                  disabled={!data.can_edit}
                  allowDeselect={false}
                  onChange={(v) => v !== null && v !== (a.metric_id ?? '') && void save(`/catalogs/metric-aliases/${a.id}`, { metric_id: v || null, needs_review: false })}
                />
                {a.needs_review && data.can_edit && (
                  <Button variant="light" onClick={() => void save(`/catalogs/metric-aliases/${a.id}`, { needs_review: false })}>
                    Верно
                  </Button>
                )}
              </Group>
            </Group>
          ))}
      </Box>
    </Stack>
  );
}

function Categories({ data, reload }: Props) {
  const mobile = useMediaQuery('(max-width: 48em)');
  const [open, setOpen] = useState<Category | 'new' | null>(null);
  const groups = data.categories.filter((c) => c.is_group);
  const items = data.categories.filter((c) => !c.is_group);
  const sections = [...groups.map((g) => ({ id: g.id, name: g.name })), { id: null, name: 'Без группы' }]
    .map((g) => ({ ...g, items: items.filter((c) => c.parent_id === g.id) }))
    .filter((g) => g.items.length);
  const ruleName = (id: string | null) => (id ? data.rules.find((r) => r.id === id)?.name : null);

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Text size="sm" c="dimmed">
          Код статьи нужен импорту и не меняется. Статью с операциями не удаляют — её выключают.
        </Text>
        {data.can_edit && (
          <Button leftSection={<IconPlus size={16} />} onClick={() => setOpen('new')}>
            Добавить статью
          </Button>
        )}
      </Group>
      {sections.map((s) => (
        <Box key={s.id ?? 'none'} className="mis-card" px="md" py={6}>
          <Text size="xs" fw={600} c="dimmed" tt="uppercase" mt={8} mb={2} style={{ letterSpacing: 0.6 }}>
            {s.name}
          </Text>
          {s.items.map((c, i) => (
            <Group key={c.id} justify="space-between" wrap="nowrap" py={8} className="mis-row" style={{ borderTop: i ? '1px solid #F0EEE9' : 0 }} onClick={() => setOpen(c)}>
              <div style={{ minWidth: 0 }}>
                <Text c={c.is_active ? undefined : 'dimmed'} td={c.is_active ? undefined : 'line-through'}>
                  {c.name}
                </Text>
                <Text size="xs" c="dimmed">
                  {KINDS[c.kind ?? ''] ?? c.kind}
                  {ruleName(c.default_allocation_rule_id) ? ` · общие делятся: ${ruleName(c.default_allocation_rule_id)}` : ''}
                </Text>
              </div>
              <Group gap={6} wrap="nowrap">
                {c.is_restricted && <Badge variant="light" color="grape">ФОТ</Badge>}
                {!c.is_active && <Badge variant="light" color="gray">выключена</Badge>}
              </Group>
            </Group>
          ))}
        </Box>
      ))}
      <Drawer opened={open !== null} onClose={() => setOpen(null)} position={mobile ? 'bottom' : 'right'} size={mobile ? '92%' : 440}
        title={<Text fw={600}>{open === 'new' ? 'Новая статья' : open?.name}</Text>}>
        {open && <CategoryForm key={open === 'new' ? 'new' : open.id} c={open === 'new' ? null : open} data={data} done={() => { setOpen(null); void reload(); }} />}
      </Drawer>
    </Stack>
  );
}

function CategoryForm({ c, data, done }: { c: Category | null; data: Catalogs; done: () => void }) {
  const [name, setName] = useState(c?.name ?? '');
  const [parent, setParent] = useState<string | null>(c?.parent_id ?? null);
  const [kind, setKind] = useState<string | null>(c?.kind ?? 'opex');
  const [restricted, setRestricted] = useState(c?.is_restricted ?? false);
  const [active, setActive] = useState(c?.is_active ?? true);
  const [rule, setRule] = useState<string | null>(c?.default_allocation_rule_id ?? null);
  const [busy, setBusy] = useState(false);
  const ro = !data.can_edit;

  async function submit() {
    setBusy(true);
    const r = c
      ? await call(() => api.patch(`/catalogs/categories/${c.id}`, {
          name, parent_id: parent, is_active: active, default_allocation_rule_id: rule,
          ...(restricted !== c.is_restricted ? { is_restricted: restricted } : {}),
        }), 'Статья сохранена')
      : await call(() => api.post('/catalogs/categories', { name, parent_id: parent, kind, is_restricted: restricted }), 'Статья добавлена');
    setBusy(false);
    if (r !== undefined) done();
  }

  return (
    <Stack gap="md">
      {c && <Text size="xs" c="dimmed">Код: {c.code} · соответствий из старых таблиц: {c.aliases}</Text>}
      <TextInput label="Название" value={name} onChange={(e) => setName(e.currentTarget.value)} disabled={ro} />
      <Select label="Группа" clearable placeholder="Без группы" data={data.categories.filter((g) => g.is_group).map((g) => ({ value: g.id, label: g.name }))} value={parent} onChange={setParent} disabled={ro} />
      {!c && <Select label="Тип" data={Object.entries(KINDS).map(([value, label]) => ({ value, label }))} value={kind} onChange={setKind} allowDeselect={false} />}
      {c && <Text size="sm">Тип: {KINDS[c.kind ?? ''] ?? c.kind}</Text>}
      <Select
        label="Как делить общие затраты по статье"
        clearable
        placeholder="Правило клиники по умолчанию"
        data={data.rules.filter((r) => r.is_active).map((r) => ({ value: r.id, label: r.name }))}
        value={rule}
        onChange={setRule}
        disabled={ro || !c}
      />
      <Switch label="Зарплатная статья (ФОТ)" description="Строки видят только собственник и финансист. Меняют признак только они." checked={restricted}
        onChange={(e) => setRestricted(e.currentTarget.checked)} disabled={!data.can_edit_finance} />
      {c && <Switch label="Действует" description="Выключенной статьи нет в форме затраты; старые операции остаются" checked={active} onChange={(e) => setActive(e.currentTarget.checked)} disabled={ro} />}
      {!ro && (
        <Button onClick={() => void submit()} loading={busy} disabled={name.trim().length < 2}>
          {c ? 'Сохранить' : 'Добавить'}
        </Button>
      )}
    </Stack>
  );
}

function Departments({ data, reload }: Props) {
  const [name, setName] = useState('');
  const [edits, setEdits] = useState<Record<string, string>>({});
  return (
    <Stack gap="md" maw={640}>
      <Box className="mis-card" px="md" py={6}>
        {data.departments.map((d, i) => {
          const value = edits[d.id] ?? d.name;
          return (
            <Group key={d.id} justify="space-between" wrap="nowrap" py={8} style={{ borderTop: i ? '1px solid #F0EEE9' : 0 }}>
              <TextInput aria-label={`Название: ${d.name}`} value={value} disabled={!data.can_edit} style={{ flex: 1 }}
                onChange={(e) => { const v = e.currentTarget.value; setEdits((x) => ({ ...x, [d.id]: v })); }} />
              {value !== d.name && (
                <Button variant="light" onClick={() => void call(() => api.patch(`/catalogs/departments/${d.id}`, { name: value }), 'Сохранено').then(() => { setEdits(({ [d.id]: _, ...rest }) => rest); return reload(); })}>
                  Сохранить
                </Button>
              )}
              <Switch aria-label={`Действует: ${d.name}`} checked={d.is_active} disabled={!data.can_edit}
                onChange={(e) => { const v = e.currentTarget.checked; void call(() => api.patch(`/catalogs/departments/${d.id}`, { is_active: v })).then(reload); }} />
            </Group>
          );
        })}
      </Box>
      {data.can_edit && (
        <Group gap="sm">
          <TextInput placeholder="Новое подразделение" aria-label="Новое подразделение" value={name} onChange={(e) => setName(e.currentTarget.value)} style={{ flex: 1 }} />
          <Button leftSection={<IconPlus size={16} />} disabled={name.trim().length < 2}
            onClick={() => void call(() => api.post('/catalogs/departments', { name }), 'Подразделение добавлено').then(() => { setName(''); return reload(); })}>
            Добавить
          </Button>
        </Group>
      )}
      <Text size="xs" c="dimmed">Выключенное подразделение пропадает из форм; его операции и показатели остаются в отчётах.</Text>
    </Stack>
  );
}

function Rules({ data, reload }: Props) {
  const { catalog } = useSession();
  const entities = catalog?.legalEntities ?? [];
  const [name, setName] = useState('');
  const [shares, setShares] = useState<Record<string, number | ''>>({});
  const total = entities.reduce((s, l) => s + Number(shares[l.id] || 0), 0);

  return (
    <Stack gap="md" maw={760}>
      <Text size="sm" c="dimmed">
        Общая затрата (ООО «Общая») делится между ООО по правилу статьи, а если у статьи его нет — по правилу клиники по умолчанию. Менять правила могут
        собственник и финансист.
      </Text>
      {data.can_edit_finance && (
        <Alert color="orange" variant="light">
          Деление считается в отчётах при каждом запросе: смена правила по умолчанию пересчитает общие затраты и прошлых месяцев.
        </Alert>
      )}
      {data.rules.map((r) => (
        <Group key={r.id} className="mis-card" p="md" justify="space-between" wrap="wrap">
          <div>
            <Group gap={6}>
              <Text fw={500}>{r.name}</Text>
              {r.is_default && <Badge variant="light">по умолчанию</Badge>}
            </Group>
            <Text size="sm" c="dimmed">
              {r.method === 'revenue_share' ? 'По выручке ООО за месяц; если выручки нет — ' : ''}
              {r.shares.map((s) => `${s.legal_entity} ${Math.round(Number(s.share) * 1000) / 10}%`).join(' · ')}
            </Text>
          </div>
          {data.can_edit_finance && !r.is_default && r.is_active && (
            <Button variant="default" onClick={() => void call(() => api.patch(`/catalogs/allocation-rules/${r.id}`, { is_default: true }), 'Правило по умолчанию изменено').then(reload)}>
              Сделать правилом по умолчанию
            </Button>
          )}
        </Group>
      ))}
      {data.can_edit_finance && (
        <Stack gap="sm" className="mis-card" p="md">
          <Text fw={500}>Новое правило с постоянными долями</Text>
          <TextInput label="Название" placeholder="Например: 70 на 30" value={name} onChange={(e) => setName(e.currentTarget.value)} />
          <Group grow>
            {entities.map((l) => (
              <NumberInput key={l.id} label={l.name} suffix=" %" min={0} max={100} value={shares[l.id] ?? ''} onChange={(v) => setShares((x) => ({ ...x, [l.id]: v === '' ? '' : Number(v) }))} />
            ))}
          </Group>
          {total !== 100 && total > 0 && <Alert color="orange" variant="light">Сумма долей {total}%, нужно 100%</Alert>}
          <Button disabled={total !== 100 || name.trim().length < 2} style={{ alignSelf: 'flex-start' }}
            onClick={() => void call(() => api.post('/catalogs/allocation-rules', {
              name, shares: entities.filter((l) => Number(shares[l.id]) > 0).map((l) => ({ legal_entity_id: l.id, share: Number(shares[l.id]) / 100 })),
            }), 'Правило добавлено').then(() => { setName(''); setShares({}); return reload(); })}>
            Добавить правило
          </Button>
        </Stack>
      )}
    </Stack>
  );
}
